/* eslint-disable no-case-declarations */
import { ClientValue, ClientName } from "./client-type";
import { Phase } from "./phase";
import { Work } from "./work";
import {
  Discount,
  Disbursement,
  Uplift,
  ExternalMatter,
  Brand,
  PriceRange,
  OfficeLocation,
  TechnologyCharge,
  TeamMembership,
} from "./";
import { FeeEarner } from "./fee-earner";
import { camelCase, pascalCase } from "change-case";
import {
  Currency,
  FindRelevantMattersQuery,
  PricingType,
  PricingLevel,
  PricingTarget,
  CollaboratorFragmentFragment,
} from "~/gql/graphql";
import { AccruedFee } from "./accrued-fee";
export type User = {
  id: number;
  userName: string;
  displayName: string;
};

export enum TaxStatus {
  None = "None",
  ClientExempt = "ClientExempt",
  MatterExempt = "MatterExempt",
  ExternalMatterExempt = "ExternalMatterExempt",
}

export declare type Matter = {
  id: number;
  template?: boolean;
  reference: string;
  client: ClientValue | null;
  externalReference: string | null;
  title: string;
  createdAt: string;
  updatedAt: string;
  status: MatterStatus;
  createdBy: User;
  phases: Phase[] | null;
  organisationalUnit: OrganisationalUnit;
  pricingType: PricingType;
  discounts: Discount[];
  disbursements: Disbursement[];
  uplifts: Uplift[] | null;
  technologyCharges: TechnologyCharge[] | null;
  pricingLevel: PricingLevel;
  externalMatterId: number | null;
  externalMatter: ExternalMatter | null;
  leadFeeEarner: FeeEarner;
  taxStatus: TaxStatus;
  brand: Brand | null;
  includeTasksInProposalOutput: boolean | null;
  includeRatesInProposalOutput: boolean | null;
  includeFeeEarnersInProposalOutput: boolean | null;
  isParent: boolean | null;
  parentMatterId: number | null;
  waiveAdminCharge?: boolean;
  waiveAdminChargeReason?: string;
  work: Work[] | null;
  priceOptions: PricingOptions | null;
  currency: Currency;
  officeLocationId?: number;
  officeLocation?: OfficeLocation;
  pricingTarget: PricingTarget | null;
  confidential?: boolean;
  teamMemberships: TeamMembership[];
  collaborators: CollaboratorFragmentFragment[];
};

export declare type MatterValue = {
  id: number;
  title: string;
  externalReference: string | null;
  client: ClientName | null;
};

export declare type MatterTemplate = Omit<
  Matter,
  | "client"
  | "externalReference"
  | "externalMatterId"
  | "externalMatter"
  | "leadFeeEarner"
  | "discounts"
  | "uplifts"
>;

export interface OrganisationalUnit {
  id: number;
  name: string;
  parent?: OrganisationalUnit | null;
}

export type PricingFields = ReturnType<typeof pricingFieldsByType>;

export type NoneRangePricingFields = {
  kind: SingleOrRange;
  gpm: number | null;
  suggested: number | null;
  chosen: number | null;
};

export type EstimatedRangePricingFields = {
  kind: SingleOrRange;
  gpm: number | null;
  suggested: PriceRange;
  chosen: PriceRange;
};

export function setSingleOrRangeProps(
  pricingType: PricingType,
  field: "single" | "lower" | "upper",
  value: number | null,
  suggestedOrChosen: "suggested" | "chosen"
) {
  const cased = {
    pricingType: camelCase(pricingType),
    field: pascalCase(field),
    estimateOrChosen: suggestedOrChosen ? pascalCase(suggestedOrChosen) : "",
  };

  const lowerUpperSingle = cased.field == "Single" ? "" : cased.field;

  return pricingType !== PricingType.EstimatedRange
    ? {
        [`${cased.pricingType}${cased.estimateOrChosen}${lowerUpperSingle}`]: value,
      }
    : {
        [`${cased.pricingType}${cased.estimateOrChosen}${lowerUpperSingle}`]: value,
      };
}

export enum SingleOrRange {
  Single,
  Range,
}

type CappedNoneRangePricingFields = {
  [Property in keyof Omit<NoneRangePricingFields, "kind"> as `capped${Capitalize<
    string & Property
  >}`]: number | null;
} & { kind: SingleOrRange.Single };

type FixedNoneRangePricingFields = {
  [Property in keyof Omit<NoneRangePricingFields, "kind"> as `fixed${Capitalize<
    string & Property
  >}`]: number | null;
} & { kind: SingleOrRange.Single };

type EstimatedNoneRangePricingFields = {
  [Property in keyof Omit<NoneRangePricingFields, "kind"> as `estimated${Capitalize<
    string & Property
  >}`]: number | null;
} & { kind: SingleOrRange.Single };

type EstimatedRangePricingFieldsFlattened = {
  kind: SingleOrRange.Range;
  estimatedRangeGpm: number | null;
  estimatedRangeSuggestedLower: number | null;
  estimatedRangeSuggestedUpper: number | null;
  estimatedRangeChosenLower: number | null;
  estimatedRangeChosenUpper: number | null;
};

type FlattenedNoneRangePricingFields =
  | CappedNoneRangePricingFields
  | FixedNoneRangePricingFields
  | EstimatedNoneRangePricingFields;

export function isRangePricingOptions(
  input: FlattenedNoneRangePricingFields | EstimatedRangePricingFieldsFlattened
): input is EstimatedRangePricingFieldsFlattened {
  return input.kind === SingleOrRange.Range;
}

type flattenedReturnType<T> = T extends PricingType.EstimatedRange
  ? EstimatedRangePricingFieldsFlattened
  : FlattenedNoneRangePricingFields;

export function pricingFieldsByTypeFlattened<T extends PricingType>(
  type: T,
  pricingOptions: PricingOptions,
  value?: number | null,
  field?: "suggested" | "chosen" | "gpm",
  lowerUpper?: "lower" | "upper"
): flattenedReturnType<T> {
  const typeCased = camelCase(type);
  const fieldCased = pascalCase(field || "");

  if (type === PricingType.None) {
    const obj = {
      kind: SingleOrRange.Single,
      fixedGpm: 0,
      fixedSuggested: 0,
      fixedChosen: 0,
    } as flattenedReturnType<T>;

    if (value != null && field) {
      return {
        ...obj,
        [camelCase(`${type}${field}`)]: value,
      } as flattenedReturnType<T>;
    }
    return obj;
  }
  if (type === PricingType.Fixed) {
    const obj = {
      kind: SingleOrRange.Single,
      fixedGpm: pricingOptions?.fixedGpm,
      fixedSuggested: pricingOptions?.fixedSuggested,
      fixedChosen: pricingOptions?.fixedChosen,
    } as flattenedReturnType<T>;

    if (value != null && field) {
      return {
        ...obj,
        [`${typeCased}${fieldCased}`]: value,
      } as flattenedReturnType<T>;
    }
    return obj;
  } else if (type === PricingType.Capped) {
    const obj = {
      kind: SingleOrRange.Single,
      cappedGpm: pricingOptions?.cappedGpm,
      cappedSuggested: pricingOptions?.cappedSuggested,
      cappedChosen: pricingOptions?.cappedChosen,
    } as flattenedReturnType<T>;

    if (value != null && field) {
      return {
        ...obj,
        [`${typeCased}${fieldCased}`]: value,
      } as flattenedReturnType<T>;
    }
    return obj;
  } else if (type === PricingType.Estimated) {
    const obj = {
      kind: SingleOrRange.Single,
      estimatedGpm: pricingOptions.estimatedGpm,
      estimatedSuggested: pricingOptions.estimatedSuggested,
      estimatedChosen: pricingOptions.estimatedChosen,
    } as flattenedReturnType<T>;

    if (value != null && field) {
      return {
        ...obj,
        [`${typeCased}${fieldCased}`]: value,
      } as flattenedReturnType<T>;
    }
    return obj;
  } else if (type === PricingType.EstimatedRange) {
    const obj = {
      kind: SingleOrRange.Range,
      estimatedRangeGpm: pricingOptions.estimatedRangeGpm,
      estimatedRangeSuggestedLower: pricingOptions.estimatedRangeSuggestedLower,
      estimatedRangeSuggestedUpper: pricingOptions.estimatedRangeSuggestedUpper,
      estimatedRangeChosenLower: pricingOptions.estimatedRangeChosenLower,
      estimatedRangeChosenUpper: pricingOptions.estimatedRangeChosenUpper,
    } as flattenedReturnType<T>;

    if (value != null && field) {
      return {
        ...obj,
        [`${typeCased}${fieldCased}${pascalCase(lowerUpper ?? "")}`]: value,
      } as flattenedReturnType<T>;
    }

    return obj;
  }
  return {
    kind: SingleOrRange.Single,
    fixedGpm: 0,
    fixedSuggested: 0,
    fixedChosen: 0,
  } as flattenedReturnType<T>;
}

type returnType<T> = T extends PricingType.EstimatedRange
  ? EstimatedRangePricingFields
  : NoneRangePricingFields;

export function pricingFieldsByType<T extends PricingType>(
  type: T,
  pricingOptions: PricingOptions | null
): returnType<T> {
  if (!pricingOptions) {
    return {
      kind: SingleOrRange.Single,
      gpm: 0,
      suggested: 0,
      chosen: 0,
    } as returnType<T>;
  }
  switch (type) {
    case PricingType.None:
      return {
        chosen: pricingOptions.fixedChosen,
        gpm: pricingOptions.fixedGpm,
        kind: SingleOrRange.Single,
        suggested: pricingOptions.fixedSuggested,
      } as returnType<T>;

    case PricingType.EstimatedRange:
      return {
        kind: SingleOrRange.Range,
        gpm: pricingOptions.estimatedRangeGpm || null,
        suggested: {
          lower: pricingOptions.estimatedRangeSuggestedLower,
          upper: pricingOptions.estimatedRangeSuggestedUpper,
        },
        chosen: {
          lower: pricingOptions.estimatedRangeChosenLower,
          upper: pricingOptions.estimatedRangeChosenUpper,
        },
      } as returnType<T>;
    case PricingType.Fixed:
      return {
        kind: SingleOrRange.Single,
        gpm: pricingOptions.fixedGpm,
        suggested: pricingOptions.fixedSuggested,
        chosen: pricingOptions.fixedChosen,
      } as returnType<T>;
    case PricingType.Capped:
      return {
        kind: SingleOrRange.Single,
        gpm: pricingOptions.cappedGpm,
        suggested: pricingOptions.cappedSuggested,
        chosen: pricingOptions.cappedChosen,
      } as returnType<T>;
    case PricingType.Estimated:
      return {
        kind: SingleOrRange.Single,
        gpm: pricingOptions.estimatedGpm,
        suggested: pricingOptions.estimatedSuggested,
        chosen: pricingOptions.estimatedChosen,
      } as returnType<T>;
    case PricingType.TopDown:
      return {
        kind: SingleOrRange.Single,
        gpm: pricingOptions.fixedGpm,
        suggested: pricingOptions.fixedSuggested,
        chosen: pricingOptions.topDownFeeTarget, // CHECK THIS
      } as returnType<T>;
    default:
      throw new Error(`Unknown pricing type: ${type}`);
  }
}

export enum LeverageType {
  WorkEffort = "WorkEffort",
  FeePercentage = "FeePercentage",
}

export const PricingTypeLabels: Record<Exclude<PricingType, PricingType.None>, string> = {
  [PricingType.Estimated]: "Estimate (single figure)",
  [PricingType.EstimatedRange]: "Estimate (range)",
  [PricingType.Fixed]: "Fixed Fee",
  [PricingType.Capped]: "Capped Fee",
  [PricingType.TopDown]: "Top Down (Testing Preview)",
};

export const PricingTypePlaceholders: Record<Exclude<PricingType, PricingType.None>, string> = {
  [PricingType.Estimated]: "Estimate",
  [PricingType.EstimatedRange]: "Low",
  [PricingType.Fixed]: "Fixed Fee",
  [PricingType.Capped]: "Capped Fee",
  [PricingType.TopDown]: "Top Down",
};

export const PricingTargetLabels: Record<PricingTarget, string> = {
  [PricingTarget.Fee]: "Fee Target",
  [PricingTarget.Hours]: "Hours Target",
};

export interface PricingSummary {
  gpm: number | null;
  suggested: number | null;
  chosen: number | null;
}
export const NULL_PRICING_SUMMARY: PricingSummary = {
  gpm: null,
  suggested: null,
  chosen: null,
};

export interface PricingSummaryRange {
  suggested: { lower: number | null; upper: number | null };
  chosen: { lower: number | null; upper: number | null };
}

export interface PricingSummaryRangeWithGPM extends PricingSummaryRange {
  gpm: number | null;
}

export type PricingOptions = {
  id: number;
  estimatedGpm: number | null;
  estimatedSuggested: number | null;
  estimatedChosen: number | null;
  estimatedRangeGpm: number | null;
  estimatedRangeSuggestedLower: number | null;
  estimatedRangeSuggestedUpper: number | null;
  estimatedRangeChosenLower: number | null;
  estimatedRangeChosenUpper: number | null;
  fixedGpm: number | null;
  fixedSuggested: number | null;
  fixedChosen: number | null;
  cappedChosen: number | null;
  cappedGpm: number | null;
  cappedSuggested: number | null;
  topDownFeeTarget: number | null;
  topDownHoursTarget: number | null;
  topDownRateDiscount: number | null;
};

export const isPricingSummary = (
  pricingSummaryOrRange: PricingSummary | PricingSummaryRange
): pricingSummaryOrRange is PricingSummary => {
  return pricingSummaryOrRange.chosen === null || typeof pricingSummaryOrRange.chosen === "number";
};

export const isPricingSummaryRange = (
  pricingSummaryOrRange: PricingSummary | PricingSummaryRange
): pricingSummaryOrRange is PricingSummary => {
  return !isPricingSummary(pricingSummaryOrRange);
};

export type BaseMatterUpdate = {
  id: number;
  isParent: boolean | null;
};

export type MatterTitleUpdate = BaseMatterUpdate & {
  title: string;
};

export type MatterOrganisationalUnitUpdate = BaseMatterUpdate & {
  organisationalUnit: OrganisationalUnit;
};

export type MatterReferenceUpdate = BaseMatterUpdate & {
  externalReference: string;
};

export type MatterStatusUpdate = BaseMatterUpdate & {
  status: MatterStatus;
};

export type MatterExternalMatterUpdate = BaseMatterUpdate & {
  externalMatterId: number | null;
};

type MatterNonExternalFieldsPreUpdateShared = {
  id: number;
  title: string;
  practiceAreaId: number;
  status: MatterStatus;
  pricingType?: PricingType;
  pricingLevel?: PricingLevel;
  pricingTarget?: PricingTarget;
  matterTaxExempt: boolean;
  officeLocationId?: number;
  confidential?: boolean;
};

type MatterNonExternalFieldsPreUpdateTemplateEdit = MatterNonExternalFieldsPreUpdateShared & {
  leadFeeEarnerId?: never;
  clientId?: never;
  externalMatterId?: never;
  templateId?: never;
  officeLocationId?: number;
};

type MatterNonExternalFieldsPreUpdateTemplateCreate = MatterNonExternalFieldsPreUpdateShared & {
  leadFeeEarnerId?: never;
  clientId?: never;
  externalMatterId?: never;
  templateId: number | undefined;
  officeLocationId?: number;
};

export type MatterNonExternalFieldsPreUpdateTemplate<T extends MatterFormMode> =
  T extends MatterFormMode.Create
    ? MatterNonExternalFieldsPreUpdateTemplateCreate
    : MatterNonExternalFieldsPreUpdateTemplateEdit;

export type MatterNonExternalFieldsPreUpdateMatterCreate =
  MatterNonExternalFieldsPreUpdateShared & {
    leadFeeEarnerId: number | null;
    clientId: number | null;
    externalMatterId: number | null;
    templateId: number | undefined;
    multiDisciplinaryMatter?: boolean;
    workspaceTitle?: string;
    parentPhaseId?: number;
    matterCurrencyId: number;
  };

type MatterNonExternalFieldsPreUpdateMatterEdit = MatterNonExternalFieldsPreUpdateShared & {
  leadFeeEarnerId: number | null;
  clientId: number | null;
  externalMatterId: number | null;
  templateId?: never;
  matterCurrencyId: number;
};

export type MatterNonExternalFieldsPreUpdateMatter<T extends MatterFormMode> =
  T extends MatterFormMode.Create
    ? MatterNonExternalFieldsPreUpdateMatterCreate
    : MatterNonExternalFieldsPreUpdateMatterEdit;

export type MatterNonExternalFieldsPreUpdate<
  T extends MatterType,
  U extends MatterFormMode
> = T extends "template"
  ? MatterNonExternalFieldsPreUpdateTemplate<U>
  : MatterNonExternalFieldsPreUpdateMatter<U>;

export type MatterNonExternalFieldsUpdate<
  T extends MatterType,
  U extends MatterFormMode
> = MatterNonExternalFieldsPreUpdate<T, U>;

export interface MatterSummary {
  id: number;
  title: string;
  client: string | null;
  updatedAt: Date;
  createdBy: string;
  status: MatterStatus;
  externalMatterId: number | null;
}

export type MatterPricingFields = {
  pricingLevel?: PricingLevel;
  pricingType?: PricingType;
  pricingTarget?: PricingTarget | null;
};

export enum MatterStatus {
  Draft = "Draft",
  ClientReview = "ClientReview",
  Live = "Live",
  Closed = "Closed",
  Lost = "Lost",
}

export const ParamToMatterStatus = {
  live: MatterStatus.Live,
  clientReview: MatterStatus.ClientReview,
  closed: MatterStatus.Closed,
  draft: MatterStatus.Draft,
  lost: MatterStatus.Lost,
};

export function urlParamToMatterStatus(param?: string | null) {
  switch (param?.toUpperCase()) {
    case "LIVE":
      return MatterStatus.Live;
    case "CLIENTREVIEW":
      return MatterStatus.ClientReview;
    case "CLOSED":
      return MatterStatus.Closed;
    case "DRAFT":
      return MatterStatus.Draft;
    case "LOST":
      return MatterStatus.Lost;
    default:
      return MatterStatus.Live;
  }
}

export const ALL_MATTER_STATUSES: MatterStatus[] = [
  MatterStatus.Draft,
  MatterStatus.ClientReview,
  MatterStatus.Live,
  MatterStatus.Closed,
  MatterStatus.Lost,
];

export const ALL_MATTER_TEMPLATE_STATUSES: MatterStatus[] = [
  MatterStatus.Draft,
  MatterStatus.Live,
  MatterStatus.Closed,
  MatterStatus.Lost,
];

export type MatterType = "matter" | "template";
export enum MatterFormMode {
  "Create",
  "Edit",
}

export type OnMatterUpdate<T extends MatterType, U extends MatterFormMode> = (
  matter: MatterNonExternalFieldsUpdate<T, U>
) => Promise<void>;

type PaginationOptions = {
  offset: number;
  limit: number;
};

interface MatterFilterInput {
  search?: string | null;
}

type QueryOptions = PaginationOptions & {
  filter: MatterFilterInput;
};

export type MatterFilterData = {
  matters: MatterValue[];
  total: number;
  queryVars: QueryOptions;
  loading: boolean;
  search: (search: string) => void;
  fetchMore: () => void;
};

export type RelevantMatter = FindRelevantMattersQuery["findRelevantMatters"]["matters"][number];
export type RelevantMatterWithAccruedFees = RelevantMatter & {
  accruedFees?: AccruedFee;
};
